import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Phone, Navigation, ChevronLeft, Info } from "lucide-react";
import api, { money, fmtErr } from "@/lib/api";
import { StarRating, Label } from "@/components/Badges";
import LocalCard from "@/components/LocalCard";
import InventoryMeter from "@/components/InventoryMeter";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function LocalDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); api.get(`/local/offers/${slug}`).then((r) => setO(r.data)); }, [slug]);
  useEffect(() => { if (user) setForm((s) => ({ ...s, name: user.name, email: user.email })); }, [user]);

  if (!o) return <div className="min-h-[60vh] grid place-items-center text-stone-400">Loading…</div>;
  const biz = o.business_info || {};

  const claim = async (e) => {
    e.preventDefault();
    setClaiming(true);
    try {
      const { data } = await api.post("/local/claim", { offer_slug: slug, name: form.name, email: form.email, phone: form.phone });
      toast.success("Friib claimed! Here's your pass 🎟️");
      nav(`/pass/${data.id}`);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); setClaiming(false); }
  };

  return (
    <div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/local" className="inline-flex items-center gap-1 text-sm font-semibold text-stone-500 hover:text-stone-900"><ChevronLeft size={16} /> Back to Friib Local</Link>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-[4/3] rounded-3xl overflow-hidden friib-shadow border border-stone-100">
            <img src={o.image} alt={o.title} className="w-full h-full object-cover" />
          </div>
          <div className="mt-4 rounded-2xl overflow-hidden border border-stone-200 friib-shadow relative h-52 bg-[#e8eef0]" data-testid="offer-map"
            style={{ backgroundImage: "linear-gradient(rgba(0,184,132,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(0,184,132,.06) 1px,transparent 1px)", backgroundSize: "36px 36px" }}>
            <div className="absolute inset-0 grid place-items-center">
              <div className="flex flex-col items-center"><MapPin size={40} className="fill-white" style={{ color: "var(--mint)" }} /><span className="mt-1 px-3 py-1 rounded-full bg-white friib-shadow text-xs font-bold">{biz.name}</span></div>
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="w-11 h-11 grid place-items-center rounded-full text-white font-display font-extrabold" style={{ background: biz.color || "var(--mint)" }}>{biz.name?.[0]}</span>
            <div><Link to={`/local?category=${biz.category}`} className="text-sm font-bold text-stone-500">{biz.name}</Link><div className="flex items-center gap-1 text-xs text-stone-400"><MapPin size={12} /> {o.distance} mi · {biz.city}</div></div>
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-stone-900 leading-tight">{o.title}</h1>
          <div className="mt-3 flex items-center gap-3"><StarRating rating={biz.rating} count={biz.review_count} /><div className="flex gap-1.5">{(o.labels || []).map((l) => <Label key={l}>{l}</Label>)}</div></div>

          <div className="mt-6 rounded-2xl bg-white border border-stone-100 friib-shadow p-6">
            <div className="flex items-center gap-3 mb-4"><span className="font-display text-4xl font-extrabold" style={{ color: "var(--mint)" }}>FRIIB</span><span className="text-stone-400 line-through">Up to {money(o.normal_value)} value</span></div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 mb-4 flex items-start gap-2"><Info size={16} className="text-amber-600 mt-0.5" /><p className="text-sm text-amber-800 font-medium">{o.purchase_requirement}</p></div>
            <InventoryMeter claimed={o.inventory_claimed} total={o.inventory_total} remaining={o.inventory_remaining} className="mb-5" />
            {!showForm ? (
              <button onClick={() => (user ? setShowForm(true) : setShowForm(true))} data-testid="claim-local-btn"
                className="w-full py-4 rounded-full font-extrabold text-white text-lg transition-transform hover:scale-[1.01]" style={{ background: "var(--mint)" }}>Claim This Friib</button>
            ) : (
              <form onSubmit={claim} className="space-y-3" data-testid="claim-local-form">
                <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="local-name" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-[var(--mint)]" />
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="local-email" className="w-full px-4 py-2.5 rounded-xl border border-stone-200 outline-none focus:border-[var(--mint)]" />
                <button disabled={claiming} className="w-full py-4 rounded-full font-extrabold text-white text-lg disabled:opacity-60" style={{ background: "var(--mint)" }}>{claiming ? "Claiming…" : "Get My Friib Pass"}</button>
              </form>
            )}
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <p className="text-stone-600 leading-relaxed">{o.description}</p>
            <div className="grid grid-cols-2 gap-3 text-stone-600">
              <span className="flex items-center gap-2"><MapPin size={15} style={{ color: "var(--mint)" }} /> {biz.address}</span>
              <span className="flex items-center gap-2"><Clock size={15} style={{ color: "var(--mint)" }} /> {biz.hours}</span>
              <span className="flex items-center gap-2"><Phone size={15} style={{ color: "var(--mint)" }} /> {biz.phone}</span>
              <span className="flex items-center gap-2"><Navigation size={15} style={{ color: "var(--mint)" }} /> {biz.website}</span>
            </div>
            <div className="rounded-xl bg-stone-50 border border-stone-100 p-4"><p className="font-bold text-stone-800 mb-1">Terms</p><p className="text-stone-500">{o.terms}</p></div>
            <div className="rounded-xl bg-stone-50 border border-stone-100 p-4"><p className="font-bold text-stone-800 mb-1">How to redeem</p><p className="text-stone-500">{o.redemption_instructions}</p></div>
          </div>
        </div>
      </div>

      {o.nearby?.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <h2 className="font-display text-2xl font-bold text-stone-900 mb-5">More Friibs Nearby</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">{o.nearby.map((n, i) => <LocalCard key={n.slug} o={n} index={i} />)}</div>
        </div>
      )}
    </div>
  );
}
